import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Package, MapPin, CheckCircle } from 'lucide-react';

interface ZeroPointCountingModalProps {
  isOpen: boolean;
  itemNo: string;
  itemDescription: string;
  binCode: string;
  pickedQuantity: number;
  expectedRemaining?: number; // Expected remaining qty after pick (0 = zero-point, >0 = few-point)
  onConfirmZero: () => void;
  onSubmitDeviation: (countedQuantity: number) => void;
  isSubmitting?: boolean;
}

export const ZeroPointCountingModal: React.FC<ZeroPointCountingModalProps> = ({
  isOpen,
  itemNo,
  itemDescription,
  binCode,
  pickedQuantity,
  expectedRemaining = 0,
  onConfirmZero,
  onSubmitDeviation,
  isSubmitting = false,
}) => {
  const [showDeviationInput, setShowDeviationInput] = useState(false);
  const [countedQuantity, setCountedQuantity] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const isZeroPoint = expectedRemaining <= 0;

  useEffect(() => {
    if (isOpen) {
      setShowDeviationInput(false);
      setCountedQuantity('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (showDeviationInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showDeviationInput]);

  const handleConfirmZero = () => {
    if (!isSubmitting) {
      if (isZeroPoint) {
        onConfirmZero();
      } else {
        // For few-point: confirm expected remaining quantity
        onSubmitDeviation(expectedRemaining);
      }
    }
  };

  const handleShowDeviationInput = () => {
    setShowDeviationInput(true);
  };

  const handleSubmitDeviation = () => {
    const quantity = parseFloat(countedQuantity);
    if (isNaN(quantity) || quantity < 0) {
      alert('Vennligst angi et gyldig antall');
      return;
    }
    onSubmitDeviation(quantity);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleSubmitDeviation();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-md overflow-y-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white px-4 py-4 sm:px-6 sm:rounded-t-lg sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <AlertTriangle size={28} />
            <div>
              <h2 className="text-xl font-bold">
                {isZeroPoint ? 'Nullpunktstelling' : 'Fåpunktstelling'}
              </h2>
              <p className="text-sm text-blue-100">
                {isZeroPoint
                  ? 'Bekreft at lokasjonen er tom'
                  : `Bekreft antall i lokasjonen (forventet: ${expectedRemaining})`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 sm:p-6 space-y-4">
          {/* Info Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
            <div className="flex items-start gap-2">
              <Package size={18} className="text-blue-700 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">{itemNo}</p>
                <p className="text-xs text-blue-700">{itemDescription}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <MapPin size={18} className="text-blue-700" />
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Lokasjon:</span> {binCode}
              </p>
            </div>

            <div className="pt-2 border-t border-blue-200">
              <p className="text-xs text-blue-800">
                Du har plukket <span className="font-semibold">{pickedQuantity}</span> stk.
                {isZeroPoint
                  ? ' Dette var siste vare på lokasjonen iht. systemet.'
                  : ` Forventet gjenstående: ${expectedRemaining} stk.`
                }
              </p>
            </div>
          </div>

          {!showDeviationInput ? (
            /* Initial buttons - Confirm or Report Deviation */
            <div className="space-y-3">
              <button
                onClick={handleConfirmZero}
                disabled={isSubmitting}
                className="w-full px-6 py-4 min-h-[44px] bg-green-600 text-white text-lg font-bold rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                <CheckCircle size={24} />
                <span>{isSubmitting ? 'Bekrefter...' : isZeroPoint
                  ? 'Bekreft 0 - Lokasjonen er tom'
                  : `Bekreft ${expectedRemaining} stk gjenstår`
                }</span>
              </button>

              <button
                onClick={handleShowDeviationInput}
                disabled={isSubmitting}
                className="w-full px-6 py-3 min-h-[44px] bg-yellow-500 text-white font-semibold rounded-lg hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isZeroPoint
                  ? 'Angi avvik - Det ligger varer her'
                  : 'Angi avvik - Antallet stemmer ikke'
                }
              </button>
            </div>
          ) : (
            /* Deviation input */
            <div className="space-y-3">
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3">
                <p className="text-sm text-yellow-900 font-semibold mb-2">
                  ⚠️ Avvik oppdaget
                </p>
                <p className="text-xs text-yellow-800">
                  Tell antall varer som faktisk ligger på lokasjonen og angi nedenfor:
                </p>
              </div>

              <div>
                <label htmlFor="countedQuantity" className="block text-sm font-semibold text-gray-700 mb-2">
                  Faktisk antall på lokasjonen:
                </label>
                <input
                  ref={inputRef}
                  id="countedQuantity"
                  type="number"
                  min="0"
                  step="0.01"
                  value={countedQuantity}
                  onChange={(e) => setCountedQuantity(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSubmitting}
                  className="w-full px-4 py-3 min-h-[44px] text-2xl font-bold text-center border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                  placeholder="0"
                />
                <p className="mt-2 text-xs text-gray-600">
                  Angi antallet du faktisk fant på lokasjonen
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSubmitDeviation}
                  disabled={isSubmitting || !countedQuantity}
                  className="flex-1 px-6 py-4 sm:py-3 min-h-[44px] bg-yellow-600 text-white text-lg font-bold rounded-lg hover:bg-yellow-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? 'Registrerer avvik...' : 'Bekreft avvik'}
                </button>
                <button
                  onClick={() => setShowDeviationInput(false)}
                  disabled={isSubmitting}
                  className="px-6 py-3 min-h-[44px] border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Tilbake
                </button>
              </div>
            </div>
          )}

          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-800 text-center">
              <strong>OBS:</strong> {isZeroPoint ? 'Nullpunktstelling' : 'Fåpunktstelling'} må fullføres før du kan fortsette med plukk.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
